import { useCallback, useEffect, useMemo, useState } from 'react';
import { MessageCircle } from 'lucide-react';
import api from '../services/api';
import { useI18n } from '../context/useI18n';
import { useAuth } from '../context/useAuth';
import { emitToast } from '../context/toast';
import { buildWaMeUrl } from '../utils/whatsappSupport';
import ExtendVigenciaModal from './admin/ExtendVigenciaModal';
import ConfirmPaymentModal from './admin/ConfirmPaymentModal';
import PartialPaymentModal from './admin/PartialPaymentModal';
import MastersBackHeader from './common/MastersBackHeader';
import { cyclesForVigenciaAssign } from '../utils/cycleUtils';
import './admin/AdminModuleVigencias.css';

/** Super admin: solo productos vigentes de esta pantalla. Gym/D28D están retirados. */
const MODULE_TABS = [
  { code: 'food', label: 'FOOD_PLAN' },
  { code: 'training', label: 'TRAINING' },
];

const COACH_MODULE_TABS = [
  { code: '', label: 'Todos' },
  { code: 'training', label: 'TRAINING' },
  { code: 'food', label: 'FOOD_PLAN' },
];

const D28D_ADMIN_MODULE_TABS = [
  { code: '', label: 'Todos' },
  { code: 'd28d', label: 'D28D' },
  { code: 'gym', label: 'Gym' },
];

const CLIENT_FILTERS = [
  { id: '', key: 'vigencias.filter_all', fallback: 'Todos' },
  { id: 'new_client', key: 'vigencias.filter_new_client', fallback: 'Nuevo' },
  { id: 'old_client', key: 'vigencias.filter_old_client', fallback: 'Antiguo' },
  { id: 'pending_payment', key: 'vigencias.filter_pending_payment', fallback: 'Pendiente por pago' },
];

/** Estados canónicos editables en Vigencias (alineados con backend vigenciaEstados.js). */
const VIGENCIA_ESTADOS = [
  { id: 'activo', label: 'Activo' },
  { id: 'inactivo', label: 'Inactivo' },
  { id: 'pago_pendiente', label: 'Pago pendiente' },
  { id: 'congelado', label: 'Congelado' },
  { id: 'pago_parcial', label: 'Pago parcial' },
];

const ESTADO_CANONICAL = {
  activo: 'activo',
  inactivo: 'inactivo',
  vencido: 'inactivo',
  cancelado: 'inactivo',
  rechazado: 'inactivo',
  suspendido: 'inactivo',
  congelado: 'congelado',
  pago_parcial: 'pago_parcial',
  pago_pendiente: 'pago_pendiente',
  pendiente: 'pago_pendiente',
  pendiente_pago_online: 'pago_pendiente',
  pendiente_sede: 'pago_pendiente',
  pendiente_habilitacion: 'pago_pendiente',
  pendiente_activacion: 'pago_pendiente',
};

const GYM_ADMIN_ROLES = ['admin_gimnasio', 'admin_marca', 'admin_gym', 'admin_sede'];

const PENDING_STATES = ['pendiente_activacion', 'pendiente_sede', 'pendiente_pago_online', 'pendiente_habilitacion', 'pendiente', 'pago_pendiente'];

function positiveId(v) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

const ESTADO_BADGE = {
  activo: 'badge-green',
  revisado: 'badge-green',
  inactivo: 'badge-slate',
  congelado: 'badge-slate',
  pago_parcial: 'badge-warning',
  pago_pendiente: 'badge-warning',
  pendiente_activacion: 'badge-warning',
  pendiente_sede: 'badge-warning',
  pendiente_pago_online: 'badge-warning',
  pendiente_habilitacion: 'badge-warning',
  pendiente: 'badge-warning',
  vencido: 'badge-danger',
};

function rowEstado(row) {
  return String(row?.estado || row?.account_estado || '').toLowerCase();
}

function canonicalEstado(raw) {
  const key = String(raw || '').toLowerCase();
  return ESTADO_CANONICAL[key] || key || '';
}

function isNewClientRow(row) {
  return row?.client_type === 'nuevo'
    || row?.is_new_client === true
    || rowEstado(row) === 'pendiente_activacion';
}

function isPendingPaymentRow(row) {
  const estado = rowEstado(row);
  const canon = canonicalEstado(estado);
  return PENDING_STATES.includes(estado) || canon === 'pago_pendiente';
}

function isOldClientRow(row) {
  if (isNewClientRow(row)) return false;
  return row?.client_type === 'antiguo' || !isPendingPaymentRow(row);
}

/** Filas overview (pending/licenses) para tipificar alertas de campanilla. */
function findAccountRowForId(data, accountId) {
  const id = Number(accountId);
  if (!Number.isFinite(id)) return null;
  const fromPending = (data?.pending || []).find((r) => Number(r.id) === id);
  if (fromPending) return fromPending;
  return (data?.licenses || []).find(
    (r) => Number(r.account_id || r.id) === id,
  ) || null;
}

/** «Confirmar pago» = cliente nuevo; «Renovar» = antiguo tipificado (admin D28D / no sede). */
function isRenewActionRow(row) {
  if (isNewClientRow(row)) return false;
  return row?.client_type === 'antiguo'
    || row?.is_new_client === false
    || isOldClientRow(row);
}

function vigenciasConfirmActionLabel(row, { isSedeScoped, t }) {
  if (isSedeScoped) return t('vigencias.activate_client', 'Activar cliente');
  if (isRenewActionRow(row)) return t('vigencias.renew', 'Renovar');
  return t('vigencias.confirm', 'Confirmar pago');
}

/** Ajusta copy de alerta in-app ya guardada: «confirma el pago» solo aplica a nuevos. */
function formatPendingAlertMessage(raw, row, t) {
  const text = String(raw || '');
  if (!text || !isRenewActionRow(row)) return text;
  return text
    .replace(
      /Revisa vigencias y confirma el pago\.?/gi,
      t('vigencias.alert_renew_suffix', 'Revisa vigencias y renueva.'),
    )
    .replace(/confirma el pago/gi, t('vigencias.renew_verb', 'renueva'));
}

function matchesClientFilter(row, clientFilter) {
  if (!clientFilter) return true;
  if (clientFilter === 'new_client') return isNewClientRow(row);
  if (clientFilter === 'old_client') return isOldClientRow(row) || row?.client_type === 'antiguo';
  if (clientFilter === 'pending_payment') return isPendingPaymentRow(row);
  return true;
}

function matchesEstadoFilter(row, estadoFilter) {
  if (!estadoFilter) return true;
  return canonicalEstado(rowEstado(row)) === String(estadoFilter);
}

function formatDateLabel(value) {
  if (!value) return null;
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return null;
  return d.toLocaleDateString();
}

function buildActivationWaUrl(row, t) {
  const phone = row?.usuario_telefono || row?.telefono;
  if (!phone) return null;
  const name = row?.usuario_nombre || '';
  const msg = isNewClientRow(row)
    ? t(
      'vigencias.wa_new_client',
      `Hola ${name}, bienvenido/a a las clases virtuales de D28D. Tu cuenta está pendiente de activación. Cualquier duda, contacta a soporte.`,
    ).replace('{name}', name)
    : t(
      'vigencias.wa_pending_payment',
      `Hola ${name}, tu servicio D28D está pendiente de pago o habilitación. Escríbenos si necesitas ayuda para activarlo.`,
    ).replace('{name}', name);
  return buildWaMeUrl(phone, msg);
}

function formatExpiryCell(lic, t) {
  const parts = [];
  const validFrom = lic.valid_from ? new Date(lic.valid_from) : null;
  const validUntil = lic.valid_until ? new Date(lic.valid_until) : null;
  const accountUntil = lic.fecha_vencimiento ? new Date(lic.fecha_vencimiento) : null;
  const displayUntil = validUntil || accountUntil;
  const now = Date.now();
  const frozen = String(lic.account_estado || '').toLowerCase() === 'congelado';

  if (validFrom && Number.isFinite(validFrom.getTime()) && validFrom.getTime() > now && !frozen) {
    parts.push(
      `${t('vigencias.starts_on', 'Inicia')} ${formatDateLabel(lic.valid_from)}`,
    );
  }
  if (displayUntil && Number.isFinite(displayUntil.getTime())) {
    const daysLeft = lic.days_left != null
      ? lic.days_left
      : Math.ceil((displayUntil.getTime() - now) / 86400000);
    const daysLabel = frozen
      ? `${daysLeft}d ${t('vigencias.frozen_days', 'congelados')}`
      : `${daysLeft}d`;
    parts.push(`${formatDateLabel(displayUntil)} (${daysLabel})`);
  } else if (frozen && lic.days_left != null) {
    parts.push(`${lic.days_left}d ${t('vigencias.frozen_days', 'congelados')}`);
  }
  if (frozen) {
    parts.push(t('vigencias.frozen_hint', 'reloj pausado'));
  }
  if (lic.cycle_deferred_until) {
    parts.push(
      `${t('vigencias.deferred_cycle', 'Ciclo desde')} ${formatDateLabel(lic.cycle_deferred_until)}`,
    );
  }
  return parts.length ? parts.join(' · ') : '—';
}

function formatWhatsappTouch(value) {
  if (!value) return null;
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return null;
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

function WhatsAppAction({ row, t, onOpened }) {
  const waUrl = buildActivationWaUrl(row, t);
  if (!waUrl) {
    return (
      <span className="text-xs text-stone-400" title={t('vigencias.no_phone', 'Sin teléfono registrado')}>
        —
      </span>
    );
  }
  return (
    <a
      href={waUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="vigencias-btn vigencias-btn--wa vigencias-btn--sm"
      title={t('vigencias.whatsapp_activate', 'Contactar por WhatsApp')}
      onClick={() => {
        onOpened?.(row);
      }}
    >
      <MessageCircle className="w-3.5 h-3.5" />
      WhatsApp
    </a>
  );
}

function licenseRowKey(lic, i) {
  return `${lic.user_id}-${lic.module_code}-${i}`;
}

function downloadLicensesCsv(rows, t) {
  const headers = [
    t('vigencias.col_user', 'Usuario'),
    'Email',
    t('vigencias.col_phone', 'Teléfono'),
    t('vigencias.col_client_type', 'Cliente'),
    t('vigencias.col_module', 'Módulo'),
    t('vigencias.col_plan', 'Plan'),
    t('vigencias.col_status', 'Estado'),
    t('vigencias.col_notes', 'Observaciones'),
    t('vigencias.col_last_wa', 'Último WA'),
    t('vigencias.col_expiry', 'Vence'),
    t('vigencias.col_days', 'Días'),
  ];
  const escape = (v) => {
    const s = String(v ?? '');
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const lines = [headers.join(',')];
  for (const lic of rows) {
    const clientLabel = (lic.client_type === 'nuevo' || lic.is_new_client) ? 'Nuevo' : 'Antiguo';
    lines.push([
      lic.usuario_nombre || '',
      lic.usuario_email || '',
      lic.usuario_telefono || '',
      clientLabel,
      (lic.module_code || '').toUpperCase(),
      lic.plan || '',
      lic.account_estado || (lic.active === false ? 'inactivo' : 'activo'),
      lic.observaciones || '',
      formatWhatsappTouch(lic.last_whatsapp_at) || '',
      formatDateLabel(lic.valid_until || lic.fecha_vencimiento) || '',
      lic.days_left != null ? lic.days_left : '',
    ].map(escape).join(','));
  }
  const blob = new Blob([`\uFEFF${lines.join('\n')}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `licencias_vigencias_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function AdminModuleVigencias({ onBack, scopeIsSede = false, initialModule = '' }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const roles = Array.isArray(user?.roles) && user.roles.length ? user.roles : [user?.rol].filter(Boolean);
  const isSuperAdmin = roles.includes('super_admin');
  const isD28dAdminOnly = roles.includes('admin_d28d') && !isSuperAdmin;
  const isGymOnlyAdmin = GYM_ADMIN_ROLES.some((r) => roles.includes(r))
    && !isSuperAdmin
    && !roles.includes('admin_d28d');
  // Sede: rol admin_sede, prop del Dashboard, o gym hijo (parent) aunque el JWT venga con admin_gimnasio.
  const isSedeScoped = scopeIsSede
    || roles.includes('admin_sede')
    || (isGymOnlyAdmin && user?.gym_parent_id != null);
  const isGymOrgView = isGymOnlyAdmin || isSedeScoped;
  const isCoachVigencias = roles.some((r) => ['entrenador', 'nutricionista', 'admin_training', 'admin_entrenador'].includes(r))
    && !isSuperAdmin
    && !roles.includes('admin_d28d')
    && !isGymOrgView;
  const visibleTabs = useMemo(() => {
    if (isGymOrgView) return [];
    if (isD28dAdminOnly) return D28D_ADMIN_MODULE_TABS;
    if (isCoachVigencias) return COACH_MODULE_TABS;
    return MODULE_TABS;
  }, [isGymOrgView, isD28dAdminOnly, isCoachVigencias]);
  const [module, setModule] = useState(() => {
    if (isCoachVigencias) return 'training';
    if (isD28dAdminOnly) return '';
    const hint = String(initialModule || '').toLowerCase();
    if (['food', 'training'].includes(hint)) return hint;
    return 'food';
  });
  const [clientFilter, setClientFilter] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('');
  const [contentTab, setContentTab] = useState(() => (
    isGymOrgView && !isSedeScoped ? 'pending' : 'alerts'
  ));
  const [statusBusyKey, setStatusBusyKey] = useState('');
  const [confirmNotes, setConfirmNotes] = useState('');
  const [waQueue, setWaQueue] = useState([]);
  const [waQueueIndex, setWaQueueIndex] = useState(0);
  const [data, setData] = useState({ pending: [], expiring: [], licenses: [], notifications: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [extendTarget, setExtendTarget] = useState(null);
  const [extendSaving, setExtendSaving] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [confirmPhase, setConfirmPhase] = useState('confirm');
  const [confirmSaving, setConfirmSaving] = useState(false);
  const [confirmResult, setConfirmResult] = useState(null);
  const [confirmCycles, setConfirmCycles] = useState([]);
  const [partialTarget, setPartialTarget] = useState(null);
  const [partialSaving, setPartialSaving] = useState(false);
  const [pendingSearch, setPendingSearch] = useState('');
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [bulkMessage, setBulkMessage] = useState(
    'Hola {nombre}, te escribimos desde D28D sobre tu plan. ¿Tienes un momento para conversar?',
  );
  const [sendingBulk, setSendingBulk] = useState(false);

  const filteredPending = useMemo(() => {
    const q = pendingSearch.trim().toLowerCase();
    let rows = (data.pending || []).filter((row) => matchesClientFilter(row, clientFilter));
    if (!q) return rows;
    return rows.filter((row) => {
      const hay = [
        row.usuario_nombre,
        row.usuario_email,
        row.plan,
        row.metodoPago,
        row.gym_id != null ? `gym ${row.gym_id}` : '',
      ].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [data.pending, pendingSearch, clientFilter]);

  const filteredLicenses = useMemo(() => (
    (data.licenses || [])
      .filter((row) => matchesClientFilter(row, clientFilter))
      .filter((row) => matchesEstadoFilter(row, estadoFilter))
  ), [data.licenses, clientFilter, estadoFilter]);

  const showExtendedPanels = !isGymOrgView || isSedeScoped;
  const contentTabs = useMemo(() => {
    const tabs = [];
    if (showExtendedPanels || (data.notifications || []).length > 0) {
      tabs.push({
        id: 'alerts',
        label: t('vigencias.admin_alerts', 'Alertas de pago pendiente'),
        short: t('vigencias.tab_alerts_short', 'Alertas'),
        count: (data.notifications || []).length,
      });
    }
    tabs.push({
      id: 'pending',
      label: t('vigencias.pending', 'Pagos pendientes'),
      short: t('vigencias.tab_pending_short', 'Pendientes'),
      count: filteredPending.length,
    });
    if (showExtendedPanels) {
      tabs.push({
        id: 'expiring',
        label: t('vigencias.expiring', 'Cuentas por vencer'),
        short: t('vigencias.tab_expiring_short', 'Por vencer'),
        count: (data.expiring || []).length,
      });
      tabs.push({
        id: 'licenses',
        label: isSedeScoped
          ? t('vigencias.sede_users', 'Usuarios de la sede')
          : t('vigencias.licenses', 'Licencias por módulo'),
        short: isSedeScoped
          ? t('vigencias.tab_sede_short', 'Usuarios')
          : t('vigencias.tab_licenses_short', 'Licencias'),
        count: filteredLicenses.length,
      });
    }
    return tabs;
  }, [
    showExtendedPanels,
    data.notifications,
    data.expiring,
    filteredPending.length,
    filteredLicenses.length,
    isSedeScoped,
    t,
  ]);

  useEffect(() => {
    if (!contentTabs.some((tab) => tab.id === contentTab)) {
      setContentTab(contentTabs[0]?.id || 'pending');
    }
  }, [contentTabs, contentTab]);

  useEffect(() => {
    setSelectedKeys(new Set());
  }, [module, clientFilter, estadoFilter, data.licenses]);

  const saveLicenseStatus = async (lic, nextEstado, nextNotes, extra = {}) => {
    const accountId = positiveId(lic.account_id || lic.id);
    if (!accountId) {
      emitToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: t('vigencias.status_no_account', 'Esta fila no tiene cuenta comercial editable.'),
      });
      return false;
    }
    const key = `${accountId}:${lic.module_code || ''}`;
    setStatusBusyKey(key);
    try {
      const body = {
        estado: nextEstado,
        observaciones: nextNotes,
      };
      if (extra.monto_parcial != null) body.monto_parcial = extra.monto_parcial;
      if (extra.currency) body.currency = extra.currency;
      await api.patch(`/payment-admin/accounts/${accountId}/status`, body);
      emitToast({
        type: 'success',
        title: t('vigencias.status_saved_title', 'Estado guardado'),
        message: extra.monto_parcial != null
          ? t('vigencias.partial_saved', 'Abono registrado y estado actualizado.')
          : t('vigencias.status_saved', 'Estado y observaciones actualizados.'),
      });
      await load();
      return true;
    } catch (err) {
      emitToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: err?.response?.data?.error || t('vigencias.status_save_error', 'No se pudo guardar el estado.'),
      });
      return false;
    } finally {
      setStatusBusyKey('');
    }
  };

  const requestEstadoChange = (lic, nextEstado) => {
    const current = canonicalEstado(lic.account_estado);
    if (nextEstado === 'pago_parcial') {
      setPartialTarget({
        lic,
        notes: lic.observaciones || '',
        userName: lic.usuario_nombre,
        plan: lic.plan,
        planAmount: lic.precio_mensual,
      });
      return;
    }
    if (nextEstado === current) return;
    saveLicenseStatus(lic, nextEstado, lic.observaciones || '');
  };

  const confirmPartialPayment = async (monto) => {
    if (!partialTarget?.lic) return;
    setPartialSaving(true);
    try {
      const ok = await saveLicenseStatus(
        partialTarget.lic,
        'pago_parcial',
        partialTarget.notes,
        { monto_parcial: monto, currency: 'COP' },
      );
      if (ok) setPartialTarget(null);
    } finally {
      setPartialSaving(false);
    }
  };

  const allFilteredSelected = filteredLicenses.length > 0
    && filteredLicenses.every((lic, i) => selectedKeys.has(licenseRowKey(lic, i)));

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedKeys(new Set());
      return;
    }
    setSelectedKeys(new Set(filteredLicenses.map((lic, i) => licenseRowKey(lic, i))));
  };

  const toggleRow = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectedLicenseRows = useMemo(() => (
    filteredLicenses.filter((lic, i) => selectedKeys.has(licenseRowKey(lic, i)))
  ), [filteredLicenses, selectedKeys]);

  const exportLicenses = () => {
    if (!filteredLicenses.length) {
      emitToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: t('vigencias.export_empty', 'No hay filas para exportar.'),
      });
      return;
    }
    downloadLicensesCsv(filteredLicenses, t);
    emitToast({
      type: 'success',
      title: t('vigencias.export_ok_title', 'Exportado'),
      message: t('vigencias.export_ok', 'Se descargó el archivo CSV (abre con Excel).'),
    });
  };

  const applyLocalWhatsappTouch = useCallback((rows, touchedAt) => {
    const stamp = touchedAt || new Date().toISOString();
    const userIds = new Set(rows.map((r) => positiveId(r.user_id)).filter(Boolean));
    const accountIds = new Set(rows.map((r) => positiveId(r.account_id || r.id)).filter(Boolean));
    setData((prev) => {
      const patchRow = (row) => {
        const uid = Number(row.user_id);
        const aid = Number(row.account_id || row.id);
        if (userIds.has(uid) || accountIds.has(aid)) {
          return { ...row, last_whatsapp_at: stamp };
        }
        return row;
      };
      return {
        ...prev,
        licenses: (prev.licenses || []).map(patchRow),
        pending: (prev.pending || []).map(patchRow),
      };
    });
  }, []);

  const recordWhatsappTouch = useCallback(async (rows) => {
    const list = (rows || []).filter(Boolean);
    if (!list.length) return;
    const user_ids = [...new Set(list.map((r) => positiveId(r.user_id)).filter(Boolean))];
    const account_ids = [...new Set(list.map((r) => positiveId(r.account_id || r.id)).filter(Boolean))];
    applyLocalWhatsappTouch(list);
    try {
      const res = await api.post('/payment-admin/whatsapp-touch', { user_ids, account_ids });
      const stamp = res.data?.data?.last_whatsapp_at;
      if (stamp) applyLocalWhatsappTouch(list, stamp);
    } catch (err) {
      console.warn('[vigencias] whatsapp-touch:', err?.message || err);
    }
  }, [applyLocalWhatsappTouch]);

  const openWhatsAppForRow = useCallback((row, template) => {
    const name = row?.usuario_nombre || '';
    const msg = String(template || '')
      .split('{nombre}').join(name)
      .split('{name}').join(name);
    const url = buildWaMeUrl(row.usuario_telefono, msg);
    if (!url) return false;
    window.open(url, '_blank', 'noopener,noreferrer');
    return true;
  }, []);

  const sendBulkWhatsApp = async () => {
    const withPhone = [];
    const seenPhones = new Set();
    let skippedNoPhone = 0;
    for (const row of selectedLicenseRows) {
      const phone = String(row.usuario_telefono || '').trim();
      if (!phone) {
        skippedNoPhone += 1;
        continue;
      }
      const dig = phone.replace(/\D/g, '');
      if (!dig || seenPhones.has(dig)) continue;
      seenPhones.add(dig);
      withPhone.push(row);
    }
    if (!withPhone.length) {
      emitToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: t('vigencias.bulk_no_phone', 'Ningún seleccionado tiene teléfono.'),
      });
      return;
    }
    const template = bulkMessage || 'Hola {nombre}, te escribimos desde D28D sobre tu plan.';
    setSendingBulk(true);
    try {
      // El navegador bloquea varios window.open: abrir el 1.º ya y encolar el resto
      // (cada «Abrir siguiente» es un gesto nuevo del usuario).
      const [first, ...rest] = withPhone.map((row) => ({
        row,
        name: row.usuario_nombre || '',
        template,
      }));
      const openedFirst = openWhatsAppForRow(first.row, template);
      if (openedFirst) {
        await recordWhatsappTouch([first.row]);
      }
      if (rest.length) {
        setWaQueue(rest);
        setWaQueueIndex(0);
        emitToast({
          type: 'success',
          title: t('vigencias.bulk_sent_title', 'WhatsApp'),
          message: t(
            'vigencias.bulk_queue_hint',
            'Se abrió 1 conversación. Quedan {n}: pulsa «Abrir siguiente» para cada una.',
            { n: rest.length },
          ),
        });
      } else {
        emitToast({
          type: 'success',
          title: t('vigencias.bulk_sent_title', 'WhatsApp'),
          message: t('vigencias.bulk_sent', 'Se abrieron {n} conversaciones.', { n: openedFirst ? 1 : 0 }),
        });
      }
      if (skippedNoPhone > 0) {
        emitToast({
          type: 'info',
          title: t('vigencias.bulk_skip_title', 'Sin teléfono'),
          message: t('vigencias.bulk_skip_phone', '{n} seleccionado(s) sin teléfono se omitieron.', { n: skippedNoPhone }),
        });
      }
    } finally {
      setSendingBulk(false);
    }
  };

  const openNextQueuedWhatsApp = async () => {
    const item = waQueue[waQueueIndex];
    if (!item) {
      setWaQueue([]);
      setWaQueueIndex(0);
      return;
    }
    const ok = openWhatsAppForRow(item.row, item.template);
    if (ok) await recordWhatsappTouch([item.row]);
    const next = waQueueIndex + 1;
    if (next >= waQueue.length) {
      setWaQueue([]);
      setWaQueueIndex(0);
      emitToast({
        type: 'success',
        title: t('vigencias.bulk_sent_title', 'WhatsApp'),
        message: t('vigencias.bulk_queue_done', 'Cola de WhatsApp completada.'),
      });
    } else {
      setWaQueueIndex(next);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const q = !isGymOrgView && module ? `?module=${module}` : '';
      const r = await api.get(`/payment-admin/overview${q}`);
      setData(r.data?.data || r.data || {});
    } catch {
      setError(t('vigencias.load_error', 'No se pudo cargar vigencias y pagos'));
    } finally {
      setLoading(false);
    }
  }, [module, t, isGymOrgView]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const params = {};
        if (isGymOrgView) {
          const gymId = user?.gym_id ?? user?.gymId ?? null;
          if (!gymId) {
            if (active) setConfirmCycles([]);
            return;
          }
          params.gym_id = gymId;
          params.gym_only = 'true';
        } else {
          params.platform_only = 'true';
        }
        const r = await api.get('/cycles', { params });
        const rows = r.data?.data || r.data || [];
        if (active) setConfirmCycles(cyclesForVigenciaAssign(Array.isArray(rows) ? rows : []));
      } catch {
        if (active) setConfirmCycles([]);
      }
    })();
    return () => { active = false; };
  }, [user?.gym_id, user?.gymId, isGymOrgView]);

  const openConfirmPayment = (accountId, opts = {}) => {
    setConfirmResult(null);
    setConfirmPhase('confirm');
    setConfirmNotes(opts.observaciones || '');
    const renewMode = opts.renewMode === true
      || (opts.renewMode !== false && opts.row != null && isRenewActionRow(opts.row) && !isSedeScoped);
    setConfirmTarget({
      accountId,
      module_code: opts.module_code || module || 'd28d',
      plan: opts.plan || '',
      cycle_id: opts.cycle_id ?? null,
      force_enable: opts.force_enable !== false,
      userName: opts.userName || '',
      userEmail: opts.userEmail || '',
      estado: opts.estado || '',
      precio_mensual: opts.precio_mensual ?? null,
      premio_label: opts.premio_label || null,
      premio_lugar: opts.premio_lugar ?? null,
      renewMode: renewMode === true,
    });
  };

  const closeConfirmPayment = () => {
    if (confirmSaving) return;
    setConfirmTarget(null);
    setConfirmResult(null);
    setConfirmPhase('confirm');
  };

  const confirmPayment = async (opts = {}) => {
    if (!confirmTarget?.accountId) return;
    const days = Number(opts.days) > 0 ? Number(opts.days) : 30;
    const cycleId = opts.cycle_id != null && opts.cycle_id !== ''
      ? opts.cycle_id
      : (confirmTarget.cycle_id ?? undefined);
    const forceEnable = opts.force_enable !== false && confirmTarget.force_enable !== false;
    setConfirmSaving(true);
    try {
      const res = await api.post(`/payment-admin/confirm/${confirmTarget.accountId}`, {
        days,
        module_code: confirmTarget.module_code || module || 'd28d',
        plan: confirmTarget.plan || undefined,
        cycle_id: cycleId,
        metodo_pago: 'confirmado_admin',
        force_enable: forceEnable === true,
        observaciones: confirmNotes || undefined,
        ...(opts.monto_cobrado != null ? { monto_cobrado: opts.monto_cobrado } : {}),
        ...(opts.precio_mensual != null ? { precio_mensual: opts.precio_mensual } : {}),
      });
      const payload = res.data?.data || {};
      setConfirmResult({ ...payload, days_granted: days });
      setConfirmPhase('success');
      const untilLabel = payload.valid_until
        ? new Date(payload.valid_until).toLocaleDateString()
        : '';
      const accountId = confirmTarget.accountId;
      setData((prev) => ({
        ...prev,
        notifications: (prev.notifications || []).filter(
          (n) => Number(n.meta?.account_id) !== Number(accountId),
        ),
        pending: (prev.pending || []).filter((row) => Number(row.id) !== Number(accountId)),
      }));
      emitToast({
        type: 'success',
        title: isSedeScoped
          ? t('vigencias.activate_success_title', 'Cliente activado')
          : t('vigencias.confirm_success_title', 'Pago confirmado'),
        message: payload.deferred
          ? t('vigencias.confirm_success_deferred', 'Pago registrado. El acceso se habilitará al iniciar el ciclo.')
          : t(
            'vigencias.confirm_success_active_until',
            'Usuario activo {n} días{until}.',
          )
            .replace('{n}', String(days))
            .replace('{until}', untilLabel ? ` · vence ${untilLabel}` : ''),
      });
      try {
        window.dispatchEvent(new CustomEvent('d28d:notifications:refresh'));
      } catch { /* ignore */ }
      load();
    } catch (e) {
      emitToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: e.response?.data?.error || t('vigencias.confirm_error', 'No se pudo confirmar'),
      });
    } finally {
      setConfirmSaving(false);
    }
  };

  const rejectPayment = async (accountId) => {
    try {
      await api.post(`/payment-admin/reject/${accountId}`, {
        module_code: module || 'd28d',
      });
      emitToast({
        type: 'success',
        title: t('vigencias.reject_success_title', 'Pago rechazado'),
        message: t('vigencias.reject_success', 'El usuario fue notificado'),
      });
      load();
    } catch (e) {
      emitToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: e.response?.data?.error || t('vigencias.reject_error', 'No se pudo rechazar'),
      });
    }
  };

  const extendLicense = (userId, moduleCode, userName = '') => {
    setExtendTarget({ userId, moduleCode, userName });
  };

  const confirmExtendLicense = async (days) => {
    if (!extendTarget) return;
    const uid = positiveId(extendTarget.userId);
    if (!uid) {
      emitToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: t('vigencias.extend_no_user', 'Esta fila no tiene usuario del ecosistema. Amplía en Food Plan.'),
      });
      setExtendTarget(null);
      return;
    }
    setExtendSaving(true);
    try {
      await api.post(`/payment-admin/extend/${uid}`, {
        module_code: extendTarget.moduleCode,
        days,
      });
      emitToast({
        type: 'success',
        title: t('vigencias.extended', 'Vigencia extendida'),
        message: `${days > 0 ? '+' : ''}${days} días en ${extendTarget.moduleCode}`,
      });
      setExtendTarget(null);
      load();
    } catch (e) {
      emitToast({
        type: 'error',
        title: t('common.error', 'Error'),
        message: e.response?.data?.error || t('users.sub_extend_error', 'No se pudo extender'),
      });
    } finally {
      setExtendSaving(false);
    }
  };

  if (loading) return <p className="d28d-text-muted">{t('vigencias.loading', 'Cargando vigencias…')}</p>;

  return (
    <div className={`vigencias-page${isGymOnlyAdmin ? ' dashboard-main-view surface-readable d28d-admin-surface p-4 sm:p-6 rounded-xl' : ''}`}>
      <div>
        {onBack ? (
          isGymOnlyAdmin ? (
            <MastersBackHeader onBack={onBack} title={t('nav.vigencias', 'Licencia')} />
          ) : (
            <button type="button" className="vigencias-btn vigencias-btn--ghost vigencias-btn--sm mb-2" onClick={onBack}>
              {t('vigencias.back_masters', 'Volver a Maestros')}
            </button>
          )
        ) : null}
        {!isGymOnlyAdmin ? (
          <>
            <h2 className="d28d-page-title">{t('vigencias.title', 'Pagos, vigencias y notificaciones')}</h2>
            <p className="d28d-text-muted">
              {t('vigencias.subtitle', 'Confirma pagos en sede, revisa vencimientos y extiende licencias por módulo (patrón FOOD_PLAN).')}
            </p>
          </>
        ) : (
          <p className="text-sm text-stone-600 mt-1">
            {isGymOrgView
              ? (isSedeScoped
                ? t('vigencias.subtitle_sede', 'Usuarios activos y pagos pendientes de tu sede.')
                : t('vigencias.subtitle_gym_pending_only', 'Pagos pendientes de usuarios de tu gimnasio y sedes (habilitación manual).'))
              : t('vigencias.subtitle_gym', 'Licencias y pagos de usuarios de tu gimnasio virtual.')}
          </p>
        )}
      </div>

      <aside className="vigencias-filters" aria-label={t('vigencias.filters_aria', 'Filtros de vigencias')}>
        {visibleTabs.length > 0 && (
          <div className="vigencias-filters__group">
            <span className="vigencias-filters__label">{t('vigencias.filter_module', 'Módulo')}</span>
            <div className="vigencias-filters__segments" role="tablist" aria-label={t('vigencias.filter_module', 'Módulo')}>
              {visibleTabs.map((tab) => {
                const active = module === tab.code;
                return (
                  <button
                    key={tab.code || 'all'}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    className={`vigencias-filters__chip${active ? ' vigencias-filters__chip--active' : ''}`}
                    onClick={() => setModule(tab.code)}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {!isGymOrgView && (
          <div className="vigencias-filters__group">
            <span className="vigencias-filters__label">{t('vigencias.filter_client', 'Cliente')}</span>
            <div className="vigencias-filters__segments" role="tablist" aria-label={t('vigencias.filter_client', 'Cliente')}>
              {CLIENT_FILTERS.map((f) => {
                const active = clientFilter === f.id;
                return (
                  <button
                    key={f.id || 'all'}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    className={`vigencias-filters__chip${active ? ' vigencias-filters__chip--active' : ''}`}
                    onClick={() => setClientFilter(f.id)}
                  >
                    {t(f.key, f.fallback)}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="vigencias-filters__group vigencias-filters__group--estado">
          <label className="vigencias-filters__label" htmlFor="vigencias-estado-filter">
            {t('vigencias.filter_estado', 'Filtrar por estado')}
          </label>
          <select
            id="vigencias-estado-filter"
            className="input vigencias-filters__select"
            value={estadoFilter}
            onChange={(e) => setEstadoFilter(e.target.value)}
          >
            <option value="">{t('vigencias.filter_all_estados', 'Todos los estados')}</option>
            {VIGENCIA_ESTADOS.map((e) => (
              <option key={e.id} value={e.id}>{e.label}</option>
            ))}
          </select>
        </div>
      </aside>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      <div className="vigencias-content surface-readable">
        <div className="vigencias-content__tabs" role="tablist" aria-label={t('vigencias.content_tabs_aria', 'Secciones de vigencias')}>
          {contentTabs.map((tab) => {
            const active = contentTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={active}
                className={`vigencias-content__tab${active ? ' vigencias-content__tab--active' : ''}${tab.id === 'alerts' ? ' vigencias-content__tab--alerts' : ''}`}
                onClick={() => setContentTab(tab.id)}
              >
                <span className="vigencias-content__tab-label">{tab.short}</span>
                <span className="vigencias-content__tab-count" aria-hidden>{tab.count}</span>
              </button>
            );
          })}
        </div>

      {contentTab === 'alerts' && (
        <section className="vigencias-panel vigencias-panel--alerts vigencias-panel--in-tabs" role="tabpanel">
          <div className="vigencias-panel__head">
            <h3 className="vigencias-panel__title">
              {t('vigencias.admin_alerts', 'Alertas de pago pendiente')}
            </h3>
          </div>
          <div className="vigencias-panel__body">
            {(data.notifications || []).length === 0 ? (
              <p className="vigencias-panel__empty">{t('vigencias.no_alerts', 'Sin alertas de pago pendiente.')}</p>
            ) : (
            <ul className="vigencias-list">
              {data.notifications.map((n) => {
                const alertRow = findAccountRowForId(data, n.meta?.account_id) || {
                  client_type: n.meta?.client_type,
                  is_new_client: n.meta?.is_new_client,
                  estado: n.meta?.estado || 'pendiente',
                };
                return (
                  <li key={n.id} className="vigencias-row">
                    <div className="vigencias-row__main">
                      {formatPendingAlertMessage(n.mensaje || n.message, alertRow, t)}
                      {(n.creado_en || n.created_at) ? (
                        <span className="vigencias-row__time">{new Date(n.creado_en || n.created_at).toLocaleString()}</span>
                      ) : null}
                    </div>
                    {n.meta?.account_id ? (
                      <div className="vigencias-row__actions">
                        <button
                          type="button"
                          className="vigencias-btn vigencias-btn--primary vigencias-btn--sm"
                          onClick={() => openConfirmPayment(n.meta.account_id, {
                            module_code: n.meta.module_code || module || 'd28d',
                            plan: n.meta.plan || '',
                            cycle_id: n.meta.cycle_id,
                            force_enable: true,
                            userName: n.meta.payer_name || alertRow?.usuario_nombre || '',
                            estado: alertRow?.estado || 'pendiente',
                            row: alertRow,
                            renewMode: !isSedeScoped && isRenewActionRow(alertRow),
                          })}
                        >
                          {vigenciasConfirmActionLabel(alertRow, { isSedeScoped, t })}
                        </button>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
            )}
          </div>
        </section>
      )}

      {contentTab === 'pending' && (
      <section className="vigencias-panel vigencias-panel--in-tabs" role="tabpanel">
        <div className="vigencias-panel__head">
          <h3 className="vigencias-panel__title">{t('vigencias.pending', 'Pagos pendientes')}</h3>
          {(data.pending || []).length > 0 && (
            <input
              type="search"
              className="input vigencias-panel__search"
              placeholder={t('vigencias.search_pending', 'Buscar por nombre o email…')}
              value={pendingSearch}
              onChange={(e) => setPendingSearch(e.target.value)}
            />
          )}
        </div>
        <div className="vigencias-panel__body">
          {data.pending?.length === 0 && (
            <p className="vigencias-panel__empty">{t('vigencias.no_pending', 'Sin pagos pendientes.')}</p>
          )}
          {data.pending?.length > 0 && filteredPending.length === 0 && (
            <p className="vigencias-panel__empty">{t('vigencias.no_pending_match', 'Ningún pago pendiente coincide con la búsqueda.')}</p>
          )}
          <div className="vigencias-list">
            {filteredPending.map((row) => (
              <div key={row.account_id || row.id || row.nest_user_id || row.usuario_email} className="vigencias-row">
                <div className="vigencias-row__main">
                  <div className="vigencias-row__name">
                    <strong>{row.usuario_nombre}</strong>
                    {isNewClientRow(row) ? (
                      <span className="vigencias-badge vigencias-badge--new">{t('vigencias.client_new', 'Nuevo')}</span>
                    ) : (
                      <span className="vigencias-badge vigencias-badge--old">{t('vigencias.client_old', 'Antiguo')}</span>
                    )}
                    {row.is_couple ? (
                      <span className="vigencias-badge vigencias-badge--soft">
                        {row.couple_role === 'invitado'
                          ? t('vigencias.couple_guest', 'Pareja (invitado)')
                          : t('vigencias.couple_owner', 'Pareja (titular)')}
                      </span>
                    ) : null}
                    {row.premio_lugar != null ? (
                      <span className="vigencias-badge vigencias-badge--prize" title={row.premio_challenge || ''}>
                        {t('vigencias.premio_badge', 'Premio')} {row.premio_label || `${row.premio_lugar}°`}
                      </span>
                    ) : null}
                  </div>
                  <div className="vigencias-row__meta">{row.usuario_email}</div>
                  <div className="vigencias-row__meta">
                    Plan {row.plan} · <span className={ESTADO_BADGE[row.estado] || 'badge-slate'}>{row.estado}</span>
                    {' · '}{row.metodoPago}
                    {row.fecha_vencimiento ? (
                      <span className="block text-xs mt-0.5">
                        {t('vigencias.col_expiry', 'Vence')}: {formatDateLabel(row.fecha_vencimiento)}
                      </span>
                    ) : null}
                    {row.cycle_deferred_until ? (
                      <span className="block text-xs text-amber-700 mt-0.5">
                        {t('vigencias.deferred_cycle', 'Ciclo desde')} {formatDateLabel(row.cycle_deferred_until)}
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="vigencias-row__actions">
                  <WhatsAppAction row={row} t={t} onOpened={(r) => recordWhatsappTouch([r])} />
                  {positiveId(row.account_id || row.id) ? (
                  <button
                    type="button"
                    className="vigencias-btn vigencias-btn--primary"
                    onClick={() => openConfirmPayment(positiveId(row.account_id || row.id), {
                      plan: row.plan,
                      cycle_id: row.cycle_id,
                      module_code: module || 'd28d',
                      force_enable: true,
                      userName: row.usuario_nombre,
                      userEmail: row.usuario_email,
                      estado: row.estado,
                      precio_mensual: row.precio_mensual,
                      premio_label: row.premio_label,
                      premio_lugar: row.premio_lugar,
                      observaciones: row.observaciones || '',
                      row,
                      renewMode: !isSedeScoped && isRenewActionRow(row),
                    })}
                  >
                    {row.estado === 'pendiente_habilitacion'
                      ? t('users.activate_user', 'Habilitar')
                      : vigenciasConfirmActionLabel(row, { isSedeScoped, t })}
                  </button>
                  ) : null}
                  {positiveId(row.account_id || row.id) ? (
                  <button
                    type="button"
                    className="vigencias-btn vigencias-btn--ghost vigencias-btn--sm"
                    onClick={() => rejectPayment(positiveId(row.account_id || row.id))}
                  >
                    {t('vigencias.reject', 'Rechazar')}
                  </button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {contentTab === 'expiring' && showExtendedPanels && (
      <section className="vigencias-panel vigencias-panel--in-tabs" role="tabpanel">
        <div className="vigencias-panel__head">
          <h3 className="vigencias-panel__title">{t('vigencias.expiring', 'Cuentas por vencer')}</h3>
        </div>
        <div className="vigencias-panel__body">
          {(data.expiring || []).length === 0 && (
            <p className="vigencias-panel__empty">{t('vigencias.no_expiring', 'Nada por vencer en los próximos 30 días.')}</p>
          )}
          {(data.expiring || []).length > 0 && (
            <ul className="vigencias-list">
              {(data.expiring || []).map((row) => (
                <li key={row.id} className="vigencias-row">
                  <div className="vigencias-row__main">
                    <span className="vigencias-row__name"><strong>{row.usuario_nombre}</strong></span>
                    <div className="vigencias-row__meta">
                      {t('vigencias.col_expiry', 'Vence')} {formatDateLabel(row.fecha_vencimiento)}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
      )}

      {contentTab === 'licenses' && showExtendedPanels && (
      <section className="vigencias-panel vigencias-panel--in-tabs" role="tabpanel">
        <div className="vigencias-panel__head">
          <h3 className="vigencias-panel__title">
            {isSedeScoped
              ? t('vigencias.sede_users', 'Usuarios de la sede')
              : t('vigencias.licenses', 'Licencias por módulo')}
          </h3>
          <button
            type="button"
            className="vigencias-btn vigencias-btn--ghost vigencias-btn--sm"
            onClick={exportLicenses}
            disabled={!filteredLicenses.length}
          >
            {t('vigencias.export_excel', 'Exportar Excel (CSV)')}
          </button>
        </div>
        <div className="vigencias-panel__body space-y-3">
        {filteredLicenses.length === 0 && (data.licenses || []).length > 0 && (
          <p className="vigencias-panel__empty">{t('vigencias.no_license_match', 'Ninguna licencia coincide con el filtro.')}</p>
        )}
        {filteredLicenses.length === 0 && !(data.licenses || []).length && (
          <p className="vigencias-panel__empty">
            {isSedeScoped
              ? t('vigencias.sede_no_users', 'Sin usuarios activos en esta sede.')
              : t('vigencias.no_licenses', 'Sin licencias para mostrar.')}
          </p>
        )}

        <div className="vigencias-bulk">
          <p className="vigencias-bulk__hint">
            {t(
              'vigencias.bulk_hint',
              'Escribe un mensaje, elige destinatarios y envía. Usa {nombre} para el nombre. Se abre el primero; el resto con «Abrir siguiente» (el navegador bloquea varias ventanas a la vez).',
            )}
          </p>
          <textarea
            className="input vigencias-bulk__input"
            value={bulkMessage}
            onChange={(e) => setBulkMessage(e.target.value)}
            placeholder={t('vigencias.bulk_placeholder', 'Mensaje para los seleccionados…')}
          />
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="vigencias-btn vigencias-btn--primary vigencias-btn--sm"
              disabled={sendingBulk || selectedKeys.size === 0}
              onClick={sendBulkWhatsApp}
            >
              {sendingBulk
                ? t('vigencias.bulk_sending', 'Abriendo…')
                : t('vigencias.bulk_send', 'Enviar WhatsApp a seleccionados ({n})', { n: selectedKeys.size })}
            </button>
            <span className="text-xs text-stone-500">
              {t('vigencias.bulk_selected', '{n} filas seleccionadas', { n: selectedKeys.size })}
            </span>
          </div>
          {waQueue.length > 0 ? (
            <div className="vigencias-bulk__queue">
              <span>
                {t(
                  'vigencias.bulk_queue_status',
                  'Cola WhatsApp {i}/{n}: {name}',
                  {
                    i: waQueueIndex + 1,
                    n: waQueue.length,
                    name: waQueue[waQueueIndex]?.name || '—',
                  },
                )}
              </span>
              <button type="button" className="vigencias-btn vigencias-btn--primary vigencias-btn--sm" onClick={openNextQueuedWhatsApp}>
                {t('vigencias.bulk_queue_next', 'Abrir siguiente')}
              </button>
              <button
                type="button"
                className="vigencias-btn vigencias-btn--ghost vigencias-btn--sm"
                onClick={() => { setWaQueue([]); setWaQueueIndex(0); }}
              >
                {t('vigencias.bulk_queue_cancel', 'Cancelar cola')}
              </button>
            </div>
          ) : null}
        </div>

        <div className="vigencias-table-wrap">
          <table className="vigencias-table">
            <thead>
              <tr>
                <th className="w-10">
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={toggleSelectAll}
                    aria-label={t('vigencias.select_all', 'Seleccionar todos')}
                  />
                </th>
                <th>{t('vigencias.col_user', 'Usuario')}</th>
                <th>{t('vigencias.col_client_type', 'Cliente')}</th>
                <th>{t('vigencias.col_plan', 'Plan')}</th>
                {!isGymOnlyAdmin ? <th>{t('vigencias.col_module', 'Módulo')}</th> : null}
                <th>{t('vigencias.col_status', 'Estado')}</th>
                <th>{t('vigencias.col_notes', 'Observaciones')}</th>
                <th>{t('vigencias.col_last_wa', 'Último WA')}</th>
                <th>{t('vigencias.col_expiry', 'Vence')}</th>
                <th>{t('vigencias.col_actions', 'Acción')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredLicenses.map((lic, i) => {
                const key = licenseRowKey(lic, i);
                const accountId = positiveId(lic.account_id);
                const busy = statusBusyKey === `${accountId}:${lic.module_code || ''}`;
                const currentCanon = canonicalEstado(lic.account_estado) || 'activo';
                return (
                  <tr key={key}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedKeys.has(key)}
                        onChange={() => toggleRow(key)}
                        aria-label={t('vigencias.select_row', 'Seleccionar')}
                      />
                    </td>
                    <td>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="vigencias-user-name">{lic.usuario_nombre}</span>
                        {lic.premio_lugar != null ? (
                          <span className="vigencias-badge vigencias-badge--prize" title={lic.premio_challenge || ''}>
                            {t('vigencias.premio_badge', 'Premio')} {lic.premio_label || `${lic.premio_lugar}°`}
                          </span>
                        ) : null}
                      </div>
                      {lic.usuario_email ? (
                        <div className="text-xs text-slate-500">{lic.usuario_email}</div>
                      ) : null}
                      {lic.is_couple ? (
                        <div className="text-xs text-slate-500 mt-0.5">
                          {lic.couple_role === 'invitado'
                            ? t('vigencias.couple_guest', 'Pareja (invitado)')
                            : t('vigencias.couple_owner', 'Pareja (titular)')}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      {isNewClientRow(lic) ? (
                        <span className="vigencias-badge vigencias-badge--new">{t('vigencias.client_new', 'Nuevo')}</span>
                      ) : (
                        <span className="vigencias-badge vigencias-badge--old">{t('vigencias.client_old', 'Antiguo')}</span>
                      )}
                    </td>
                    <td>{lic.plan || '—'}</td>
                    {!isGymOnlyAdmin ? (
                      <td className="uppercase tracking-wide text-slate-600">{lic.module_code}</td>
                    ) : null}
                    <td>
                      {accountId ? (
                        <select
                          className="input text-xs min-w-[9rem]"
                          value={currentCanon}
                          disabled={busy}
                          onChange={(e) => requestEstadoChange(lic, e.target.value)}
                        >
                          {VIGENCIA_ESTADOS.map((e) => (
                            <option key={e.id} value={e.id}>{e.label}</option>
                          ))}
                          {!VIGENCIA_ESTADOS.some((e) => e.id === currentCanon) && lic.account_estado ? (
                            <option value={currentCanon}>{lic.account_estado}</option>
                          ) : null}
                        </select>
                      ) : (
                        <span className={ESTADO_BADGE[lic.account_estado] || 'badge-slate'}>
                          {lic.account_estado || t('vigencias.status_active', 'activo')}
                        </span>
                      )}
                      {accountId && currentCanon === 'pago_parcial' ? (
                        <button
                          type="button"
                          className="vigencias-btn vigencias-btn--ghost vigencias-btn--sm mt-1"
                          disabled={busy}
                          onClick={() => requestEstadoChange(lic, 'pago_parcial')}
                        >
                          {t('vigencias.partial_again', 'Registrar abono')}
                        </button>
                      ) : null}
                    </td>
                    <td className="min-w-[12rem]">
                      {accountId ? (
                        <textarea
                          className="input text-xs w-full min-h-[52px]"
                          defaultValue={lic.observaciones || ''}
                          key={`${accountId}-${lic.observaciones || ''}`}
                          disabled={busy}
                          placeholder={t('vigencias.notes_ph_short', 'Observación…')}
                          onBlur={(e) => {
                            const next = e.target.value;
                            if (String(next || '') === String(lic.observaciones || '')) return;
                            saveLicenseStatus(lic, currentCanon || 'activo', next);
                          }}
                        />
                      ) : (lic.observaciones || '—')}
                    </td>
                    <td className="text-xs text-slate-600 whitespace-nowrap">
                      {formatWhatsappTouch(lic.last_whatsapp_at) || '—'}
                    </td>
                    <td>{formatExpiryCell(lic, t)}</td>
                    <td>
                      <div className="vigencias-row__actions">
                        <WhatsAppAction row={lic} t={t} onOpened={(r) => recordWhatsappTouch([r])} />
                        {positiveId(lic.user_id) ? (
                        <button
                          type="button"
                          className="vigencias-btn vigencias-btn--ghost vigencias-btn--sm"
                          onClick={() => extendLicense(positiveId(lic.user_id), lic.module_code, lic.usuario_nombre)}
                        >
                          {t('vigencias.extend', 'Extender')}
                        </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </div>
      </section>
      )}
      </div>

      <PartialPaymentModal
        open={Boolean(partialTarget)}
        saving={partialSaving}
        userName={partialTarget?.userName}
        plan={partialTarget?.plan}
        planAmount={partialTarget?.planAmount}
        onClose={() => !partialSaving && setPartialTarget(null)}
        onConfirm={confirmPartialPayment}
        t={t}
      />
      <ExtendVigenciaModal
        open={Boolean(extendTarget)}
        userName={extendTarget?.userName}
        moduleLabel={(extendTarget?.moduleCode || 'd28d').toUpperCase()}
        saving={extendSaving}
        onClose={() => !extendSaving && setExtendTarget(null)}
        onConfirm={confirmExtendLicense}
      />
      <ConfirmPaymentModal
        open={Boolean(confirmTarget)}
        phase={confirmPhase}
        saving={confirmSaving}
        userName={confirmResult?.usuario_nombre || confirmTarget?.userName}
        userEmail={confirmResult?.usuario_email || confirmTarget?.userEmail}
        plan={confirmResult?.plan || confirmTarget?.plan}
        cycleId={confirmResult?.cycle_id ?? confirmTarget?.cycle_id}
        cycles={confirmCycles}
        estado={confirmPhase === 'success'
          ? (confirmResult?.account?.estado || 'activo')
          : confirmTarget?.estado}
        validUntil={confirmResult?.valid_until}
        deferred={confirmResult?.deferred === true}
        daysGranted={confirmResult?.days_granted}
        message={confirmResult?.message}
        observaciones={confirmNotes}
        onObservacionesChange={setConfirmNotes}
        onClose={closeConfirmPayment}
        onConfirm={confirmPayment}
        activateMode={isSedeScoped}
        renewMode={Boolean(confirmTarget?.renewMode) && !isSedeScoped}
        defaultAmount={confirmTarget?.precio_mensual}
        planBaseAmount={confirmTarget?.precio_mensual}
        premioLabel={confirmTarget?.premio_label || null}
        t={t}
      />
    </div>
  );
}
